// Shared "Enable notifications" controls. Rendered on the notifications page
// (and re-rendered whenever the daemon's VAPID key arrives) so a single source
// drives the push-enablement UI across views.
import { el } from "./dom.js";
import { Connection } from "./relay.js";
import { enablePush, syncPush } from "./push.js";
import { pushUnavailableReason, type PushEnv } from "./pushstatus.js";

export async function renderPushControls(slot: HTMLElement, conn: Connection): Promise<void> {
  const env: PushEnv = {
    hasServiceWorker: "serviceWorker" in navigator,
    hasPushManager: "PushManager" in window,
    hasNotification: "Notification" in window,
    permission: (typeof Notification !== "undefined" ? Notification.permission : "default") as "default" | "granted" | "denied",
    ios: /iphone|ipad|ipod/i.test(navigator.userAgent),
    standalone: matchMedia("(display-mode: standalone)").matches || (navigator as any).standalone === true,
  };

  const reason = pushUnavailableReason(env);

  if (reason !== null) {
    slot.replaceChildren(el("div", { class: "meta" }, [reason]));
    return;
  }

  if (env.permission === "granted") {
    void syncPush(conn);
    slot.replaceChildren();
    return;
  }

  // Race fix: the VAPID key arrives asynchronously after connect (a "vapid"
  // frame). Tapping Enable before it lands throws "hasn't sent its push key
  // yet". So hold a placeholder until the key is present — the caller re-renders
  // this slot on onVapid, which swaps in the real button the moment it arrives.
  if (!conn.vapidKey) {
    slot.replaceChildren(el("div", { class: "meta" }, ["Preparing notifications…"]));
    return;
  }

  const btn = el("button", { class: "primary" }, ["Enable notifications"]) as HTMLButtonElement;
  btn.onclick = async () => {
    btn.disabled = true;
    try {
      await enablePush(conn);
      slot.replaceChildren(el("div", { class: "notice" }, ["Notifications on — you'll get a ping when Fabrika needs you."]));
    } catch (e) {
      btn.disabled = false;
      slot.replaceChildren(el("div", { class: "error-banner" }, [(e as Error).message]), btn);
    }
  };
  slot.replaceChildren(
    el("div", { class: "notice" }, [
      "Get a notification when Fabrika needs a decision or approval. ",
      btn,
    ]),
  );
}
